import React, { useState } from 'react';
import { Download, FileText, ExternalLink, X, Eye, Loader2, AlertCircle, RefreshCw } from 'lucide-react';
import { formatFileSize } from './attachmentUtils';
import { usePresignedFileUrl } from './usePresignedFileUrl';

interface PdfAttachmentProps {
  fileName: string;
  url: string;
  fileSize?: number | undefined;
  workspaceId?: string | undefined;
  fileId?: string | undefined;
}

export const PdfAttachment: React.FC<PdfAttachmentProps> = ({
  fileName,
  url,
  fileSize,
  workspaceId,
  fileId,
}) => {
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  const presigned = usePresignedFileUrl(workspaceId, fileId);

  const activeUrl = presigned.url || url;
  const isLoading = presigned.isLoading;
  const isError = presigned.hasError;

  const backendDownloadUrl = (workspaceId && fileId)
    ? `/api/v1/workspaces/${workspaceId}/files/${fileId}/download?disposition=attachment`
    : activeUrl;

  const handleRetry = async () => {
    await presigned.refresh(true);
  };

  return (
    <div className="my-2 max-w-md select-none">
      <div className="rounded-2xl border border-red-500/30 bg-card overflow-hidden shadow-sm transition hover:shadow-md">
        {/* Banner card header */}
        <div className="p-3.5 bg-gradient-to-r from-red-500/10 via-rose-500/5 to-transparent flex items-center justify-between gap-3 border-b border-border/60">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 rounded-xl bg-red-500/15 text-red-500 flex items-center justify-center flex-shrink-0 shadow-xs">
              <FileText className="h-5 w-5" />
            </div>
            <div className="min-w-0">
              <p className="font-bold text-foreground truncate text-xs sm:text-sm">{fileName}</p>
              <p className="text-[11px] text-muted-foreground font-medium">
                PDF Document • {formatFileSize(fileSize || presigned.fileSize)}
              </p>
            </div>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="px-3.5 py-2 bg-card flex items-center justify-end gap-2 text-xs">
          <button
            type="button"
            onClick={() => setIsPreviewOpen(true)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-muted hover:bg-muted/80 text-foreground font-semibold transition"
          >
            <Eye className="h-3.5 w-3.5" />
            <span>Preview</span>
          </button>

          <a
            href={backendDownloadUrl}
            download={fileName}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-primary text-primary-foreground font-semibold transition shadow-xs"
            onClick={(e) => e.stopPropagation()}
          >
            <Download className="h-3.5 w-3.5" />
            <span>Download</span>
          </a>
        </div>
      </div>

      {/* PDF Modal Viewer */}
      {isPreviewOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 animate-in fade-in"
          onClick={() => setIsPreviewOpen(false)}
        >
          <div
            className="w-full max-w-4xl h-[85vh] rounded-2xl bg-card border border-border shadow-2xl flex flex-col overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="px-5 py-3 border-b border-border flex items-center justify-between bg-card">
              <div className="flex items-center gap-2.5 min-w-0">
                <FileText className="h-4.5 w-4.5 text-red-500 flex-shrink-0" />
                <span className="font-bold text-xs sm:text-sm text-foreground truncate max-w-md">
                  {fileName}
                </span>
              </div>

              <div className="flex items-center gap-2">
                {activeUrl && (
                  <a
                    href={activeUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition"
                    title="Open in new tab"
                  >
                    <ExternalLink className="h-4 w-4" />
                  </a>
                )}

                <a
                  href={backendDownloadUrl}
                  download={fileName}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-primary text-primary-foreground font-semibold text-xs transition"
                >
                  <Download className="h-3.5 w-3.5" />
                  <span>Download</span>
                </a>

                <button
                  type="button"
                  onClick={() => setIsPreviewOpen(false)}
                  className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            {/* Modal Content */}
            <div className="flex-1 bg-slate-950 flex items-center justify-center overflow-hidden">
              {isLoading ? (
                <div className="flex flex-col items-center justify-center gap-3 text-muted-foreground">
                  <Loader2 className="h-8 w-8 animate-spin text-red-500" />
                  <p className="text-xs font-semibold">Resolving secure PDF preview...</p>
                </div>
              ) : isError || !activeUrl ? (
                <div className="flex flex-col items-center justify-center p-6 text-center max-w-md gap-3">
                  <AlertCircle className="h-10 w-10 text-red-400" />
                  <p className="font-bold text-foreground text-sm">Preview Unavailable</p>
                  <p className="text-xs text-muted-foreground">
                    Unable to load secure preview for this PDF document. You can still download the original file.
                  </p>
                  <div className="flex items-center gap-2 mt-2">
                    <button
                      type="button"
                      onClick={handleRetry}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-muted hover:bg-muted/80 text-foreground font-semibold text-xs transition"
                    >
                      <RefreshCw className="h-3.5 w-3.5" />
                      <span>Retry</span>
                    </button>
                    <a
                      href={backendDownloadUrl}
                      download={fileName}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-primary text-primary-foreground font-semibold text-xs transition"
                    >
                      <Download className="h-3.5 w-3.5" />
                      <span>Download PDF</span>
                    </a>
                  </div>
                </div>
              ) : (
                <iframe
                  key={activeUrl}
                  src={activeUrl}
                  title={fileName}
                  className="w-full h-full border-none rounded-b-2xl bg-white"
                />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
